(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-collab-product-details]';
  const SECTION_KEYS = ['important', 'description', 'fabric', 'sizing', 'shipping'];
  const ACCORDION_LABELS = {
    description: 'Description',
    fabric: 'Fabric & Care',
    sizing: 'Sizing Information',
    shipping: 'Shipping & Returns',
  };

  const LABELS = new Map([
    ['IMPORTANT DESCRIPTION', 'important'],
    ['IMPORTANT', 'important'],
    ['DESCRIPTION', 'description'],
    ['PRODUCT DESCRIPTION', 'description'],
    ['FABRIC & CARE', 'fabric'],
    ['FABRIC AND CARE', 'fabric'],
    ['SIZING INFORMATION', 'sizing'],
    ['SIZE & FIT', 'sizing'],
    ['SIZE AND FIT', 'sizing'],
    ['SHIPPING & RETURNS', 'shipping'],
    ['SHIPPING AND RETURNS', 'shipping'],
    ['RETURNS & SHIPPING', 'shipping'],
    ['RETURNS AND SHIPPING', 'shipping'],
  ]);

  const normalize = (value = '') =>
    value
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/:$/, '')
      .toUpperCase();

  function getMarker(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return null;
    const element = /** @type {HTMLElement} */ (node);
    const marker = LABELS.get(normalize(element.textContent || ''));
    if (!marker) return null;
    return /^(H[1-6]|P|DIV)$/.test(element.tagName) ? marker : null;
  }

  function nodeIsVisuallyEmpty(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      return !node.textContent.replace(/\u00a0/g, ' ').trim();
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    const element = /** @type {HTMLElement} */ (node);
    const hasNonTextContent = Boolean(
      element.querySelector('img, video, iframe, svg, table, ul, ol, blockquote, hr')
    );
    return !element.textContent.replace(/\u00a0/g, ' ').trim() && !hasNonTextContent;
  }

  function trimEmptyEdges(container) {
    while (container.firstChild && nodeIsVisuallyEmpty(container.firstChild)) {
      container.firstChild.remove();
    }
    while (container.lastChild && nodeIsVisuallyEmpty(container.lastChild)) {
      container.lastChild.remove();
    }
  }

  function hasMeaningfulContent(container) {
    const text = container.textContent.replace(/\u00a0/g, ' ').trim();
    const hasNonTextContent = Boolean(
      container.querySelector('img, video, iframe, svg, table, ul, ol, blockquote, hr')
    );
    return Boolean(text || hasNonTextContent);
  }

  function makeSizeGuide(url) {
    const figure = document.createElement('figure');
    figure.className = 'collab-size-guide';
    const image = document.createElement('img');
    image.className = 'collab-size-guide__image';
    image.src = url;
    image.alt = 'V. Chapman size guide';
    image.width = 480;
    image.height = 267;
    image.loading = 'lazy';
    image.decoding = 'async';
    figure.appendChild(image);
    return figure;
  }

  function makePanel(key, content, sizeGuideUrl, usedLegacyFallback) {
    const panel = document.createElement('details');
    panel.className = 'collab-product-accordion';
    panel.dataset.collabPanel = key;
    if (key === 'description') panel.open = true;

    const summary = document.createElement('summary');
    summary.className = 'collab-product-accordion__summary';

    // <summary> permits one heading child. Put both title and icon INSIDE H2
    // to keep semantic headings without changing the native details control.
    const heading = document.createElement('h2');
    heading.className = 'collab-product-accordion__title';
    heading.appendChild(document.createTextNode(ACCORDION_LABELS[key]));
    const icon = document.createElement('span');
    icon.className = 'collab-product-accordion__icon';
    icon.setAttribute('aria-hidden', 'true');
    heading.appendChild(icon);
    summary.appendChild(heading);
    panel.appendChild(summary);

    const contentContainer = document.createElement('div');
    contentContainer.className = 'collab-product-accordion__content';
    const body = document.createElement('div');
    body.className = 'rte';
    body.dataset.collabContent = key;
    while (content.firstChild) body.appendChild(content.firstChild);
    contentContainer.appendChild(body);
    if (key === 'sizing' && !usedLegacyFallback && sizeGuideUrl) {
      contentContainer.appendChild(makeSizeGuide(sizeGuideUrl));
    }
    panel.appendChild(contentContainer);
    return panel;
  }

  function initialize(root) {
    if (root.dataset.collabInitialized === 'true') return;

    const source = root.querySelector('[data-collab-source]');
    const accordions = root.querySelector('[data-collab-accordions]');
    if (!source || !accordions) return;

    const fragments = {};
    SECTION_KEYS.forEach((key) => { fragments[key] = document.createDocumentFragment(); });

    // Move (never clone) each authored content node. This avoids retaining
    // multiple copies of the product description in the rendered DOM.
    let activeKey = 'description';
    Array.from(source.childNodes).forEach((node) => {
      const marker = getMarker(node);
      if (marker) {
        activeKey = marker;
        node.remove();
      } else {
        fragments[activeKey].appendChild(node);
      }
    });

    const important = root.querySelector('[data-collab-important]');
    const importantBody = root.querySelector('[data-collab-content="important"]');
    if (important && importantBody) {
      importantBody.replaceChildren(fragments.important);
      trimEmptyEdges(importantBody);
      if (hasMeaningfulContent(importantBody)) important.hidden = false;
    }

    const newPanels = document.createDocumentFragment();
    let visibleAccordionCount = 0;
    ['description', 'fabric', 'sizing', 'shipping'].forEach((key) => {
      // Check authored copy first; use a legacy fallback only when it is absent.
      const temporary = document.createElement('div');
      temporary.appendChild(fragments[key]);
      trimEmptyEdges(temporary);
      let hasContent = hasMeaningfulContent(temporary);
      let usedLegacyFallback = false;
      if (!hasContent) {
        const fallback = root.querySelector(`template[data-collab-fallback="${key}"]`);
        if (fallback?.content) {
          temporary.appendChild(fallback.content.cloneNode(true));
          trimEmptyEdges(temporary);
          hasContent = hasMeaningfulContent(temporary);
          usedLegacyFallback = hasContent;
        }
      }
      if (!hasContent) return;
      newPanels.appendChild(makePanel(key, temporary, root.dataset.collabSizeGuideUrl, usedLegacyFallback));
      visibleAccordionCount += 1;
    });

    accordions.replaceChildren(newPanels);
    if (visibleAccordionCount > 0) {
      accordions.hidden = false;
      root.classList.add('has-accordions');
    }
    // All original DOM nodes are now in their final, accessible destinations.
    source.remove();
    root.dataset.collabInitialized = 'true';
  }

  function initializeAll(scope = document) {
    if (scope.matches?.(ROOT_SELECTOR)) initialize(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
